/**
 * Conditional logging utility for development vs production
 * In production, logs are suppressed to reduce console noise and prevent data leakage
 *
 * SECURITY: Never log sensitive data like tokens, passwords, or full user objects
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Sanitize arguments to remove potentially sensitive data
 * This runs even in development to catch accidental sensitive data logging
 */
const sanitizeArgs = (args: any[]): any[] => {
  return args.map(arg => {
    if (typeof arg === 'string') {
      // Mask anything that looks like a token or key
      return arg
        .replace(/Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi, 'Bearer [REDACTED]')
        .replace(/token[=:]\s*["']?[A-Za-z0-9\-_]+["']?/gi, 'token=[REDACTED]')
        .replace(/password[=:]\s*["']?[^"'\s]+["']?/gi, 'password=[REDACTED]');
    }
    if (typeof arg === 'object' && arg !== null) {
      // Don't log objects that might contain sensitive data in production
      if (!isDevelopment) {
        // In production, only log safe properties
        const safeKeys = ['id', 'cardId', 'retroId', 'sessionId', 'status', 'type', 'message', 'name'];
        const safeObj: Record<string, any> = {};
        for (const key of safeKeys) {
          if (key in arg) {
            safeObj[key] = arg[key];
          }
        }
        return Object.keys(safeObj).length > 0 ? safeObj : '[Object]';
      }
    }
    return arg;
  });
};

export const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...sanitizeArgs(args));
    }
  },

  error: (...args: any[]) => {
    // Log errors in production but sanitize sensitive data
    if (isDevelopment) {
      console.error(...args);
    } else {
      // In production: only log the error message, not full stack traces or objects
      const sanitized = args.map(arg => {
        if (arg instanceof Error) {
          return arg.message; // Only the message, not the stack
        }
        if (typeof arg === 'object' && arg !== null) {
          return '[Error details hidden in production]';
        }
        return arg;
      });
      console.error(...sanitized);
    }
  },

  warn: (...args: any[]) => {
    if (isDevelopment) {
      console.warn(...sanitizeArgs(args));
    }
  },

  debug: (...args: any[]) => {
    if (isDevelopment) {
      console.debug(...sanitizeArgs(args));
    }
  },

  info: (...args: any[]) => {
    if (isDevelopment) {
      console.info(...sanitizeArgs(args));
    }
  }
};
